<script setup lang="ts">
/**
 * "แชทเก่าจาก LINE" in the customer panel of a live LINE chat (api/app/line_link.py): the customer's old chat from the
 * LINE OA export — linked by the system when a number they typed matches them one-to-one, or confirmed by staff.
 * A suggestion (same name, or a number shared with others) asks "ใช่คนเดียวกันไหม" with ใช่ / ไม่ใช่ (everyone);
 * a link shows who made it, and owner / head can undo a wrong one. Hidden when there is nothing to show.
 */
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { lineHistoryApi, prepareLineHistoryChat, type HistoryRoom, type LineHistoryView } from '../../api/linehistory'
import type { ConversationDetail } from '../../api/types'
import { countText, linkedByText, rangeText, roomName, spanText, suggestTitle } from '../../lib/lineHistory'
import { useInbox } from '../../stores/inbox'
import Icon from '../ui/Icon.vue'

const props = defineProps<{ detail: ConversationDetail }>()
const store = useInbox()
const router = useRouter()

const view = ref<LineHistoryView | null>(null)
const error = ref<string | null>(null)
const busy = ref<string | null>(null) // history room being answered / unlinked
const confirming = ref<string | null>(null)
let seq = 0

async function load(): Promise<void> {
  const id = props.detail.id
  const mine = ++seq
  try {
    const v = await lineHistoryApi().view(id)
    if (mine !== seq) return
    view.value = v
    error.value = null
  } catch (e) {
    if (mine !== seq) return
    error.value = e instanceof Error ? e.message : String(e)
  }
}

watch(
  () => props.detail.id,
  () => {
    view.value = null
    error.value = null
    confirming.value = null
    void load()
  },
  { immediate: true },
)

const live = computed(() => (view.value?.kind === 'live' ? view.value : null))
const linked = computed(() => live.value?.linked ?? [])
const suggested = computed(() => live.value?.suggested ?? [])
const shown = computed(() => linked.value.length > 0 || suggested.value.length > 0)

function open(r: HistoryRoom): void {
  prepareLineHistoryChat(props.detail.id, r.conversation_id)
  void router.push({ name: 'conversation', params: { id: r.conversation_id } })
}

async function reply(r: HistoryRoom, same: boolean): Promise<void> {
  if (busy.value) return
  const id = props.detail.id
  busy.value = r.conversation_id
  try {
    const v = await lineHistoryApi().answer(id, r.conversation_id, same)
    if (props.detail.id === id) view.value = v // another chat opened meanwhile: its own card stays
    store.flash(same ? 'ผูกแชทเก่ากับลูกค้าคนนี้แล้ว' : 'รับทราบค่ะ จะไม่แนะนำแชทนี้ให้ลูกค้าคนนี้อีก')
  } catch (e) {
    store.flash(e instanceof Error ? e.message : String(e))
    await load()
  } finally {
    busy.value = null
  }
}

async function unlink(r: HistoryRoom): Promise<void> {
  if (busy.value) return
  const id = props.detail.id
  busy.value = r.conversation_id
  try {
    const v = await lineHistoryApi().unlink(id, r.conversation_id)
    if (props.detail.id === id) view.value = v
    confirming.value = null
    store.flash('ยกเลิกการผูกแล้ว ระบบจะไม่ผูกแชทนี้กับลูกค้าคนนี้อีก')
  } catch (e) {
    store.flash(e instanceof Error ? e.message : String(e))
    await load()
  } finally {
    busy.value = null
  }
}
</script>

<template>
  <section v-if="shown || (error && live)" class="cs lhh" aria-label="แชทเก่าจาก LINE">
    <div class="sec-h">
      <Icon name="history" class="lhh-ic" />
      <h3 class="sec-t">แชทเก่าจาก LINE</h3>
    </div>

    <p v-if="error" class="lhh-l t-warn"><Icon name="alertCircle" /><span>โหลดแชทเก่าไม่สำเร็จ: {{ error }}</span></p>

    <div v-for="r in linked" :key="r.conversation_id" class="lhh-room">
      <div class="lhh-sum">
        <div class="lhh-sum-b">
          <span class="lhh-n">{{ countText(r.messages) }}</span>
          <span v-if="spanText(r.first_at, r.last_at)" class="lhh-note">{{ spanText(r.first_at, r.last_at) }}</span>
          <span class="lhh-note">{{ linkedByText(r) }}</span>
        </div>
        <button type="button" class="lhh-open" aria-label="เปิดดูแชทเก่าจาก LINE" @click="open(r)">
          <span>เปิดดูแชทเก่า</span><Icon name="arrowRight" />
        </button>
      </div>
      <template v-if="live?.can_unlink">
        <div v-if="confirming === r.conversation_id" class="lhh-confirm" role="group" aria-label="ยืนยันยกเลิกการผูก">
          <span class="lhh-confirm-t">ยกเลิกการผูกแชทเก่านี้กับลูกค้าคนนี้ใช่ไหม</span>
          <div class="lhh-acts">
            <button type="button" class="lhh-btn warn" :disabled="!!busy" @click="unlink(r)">
              <Icon name="undo" />{{ busy === r.conversation_id ? 'กำลังยกเลิก…' : 'ยกเลิกการผูก' }}
            </button>
            <button type="button" class="lhh-btn" :disabled="!!busy" @click="confirming = null">ไม่ยกเลิก</button>
          </div>
        </div>
        <button v-else type="button" class="lhh-text" @click="confirming = r.conversation_id">
          <Icon name="undo" />ไม่ใช่คนเดียวกัน · ยกเลิกการผูก
        </button>
      </template>
    </div>

    <div v-for="r in suggested" :key="r.conversation_id" class="lhh-sug">
      <p class="lhh-q"><Icon name="helpCircle" /><span>{{ suggestTitle(r) }}</span></p>
      <div class="lhh-sug-b">
        <span class="lhh-name">{{ roomName(r) }}</span>
        <span class="lhh-note">{{ rangeText(r) }}</span>
      </div>
      <div class="lhh-acts">
        <button type="button" class="lhh-btn yes" :disabled="!!busy" @click="reply(r, true)"><Icon name="check" />ใช่</button>
        <button type="button" class="lhh-btn" :disabled="!!busy" @click="reply(r, false)"><Icon name="x" />ไม่ใช่</button>
        <span class="grow"></span>
        <button type="button" class="lhh-text" @click="open(r)">เปิดดูก่อน<Icon name="arrowRight" /></button>
      </div>
    </div>
  </section>
</template>

<style scoped>
.lhh {
  gap: 10px;
}
.lhh-ic {
  width: 16px;
  height: 16px;
  color: var(--mut);
}
.grow {
  flex: 1;
}
.lhh-room {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.lhh-sum {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 8px 10px 12px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: #fbfcfb;
}
.lhh-sum-b,
.lhh-sug-b {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.lhh-n,
.lhh-name {
  font-size: 13.5px;
  font-weight: 600;
  line-height: 21px;
  color: var(--ink);
  overflow-wrap: anywhere;
}
.lhh-note {
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
.lhh-open {
  flex: none;
  height: 34px;
  padding: 0 10px 0 12px;
  border-radius: 9px;
  border: 1px solid #bfdccd;
  background: #fff;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  line-height: 20px;
  font-weight: 600;
  color: var(--ac2);
  white-space: nowrap;
}
.lhh-open:hover {
  background: var(--acs);
}
.lhh-open :deep(.ic),
.lhh-text :deep(.ic),
.lhh-btn :deep(.ic) {
  width: 14px;
  height: 14px;
}
.lhh-sug {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px 10px;
  border: 1px solid #e6dcc4;
  border-radius: 10px;
  background: #fffbf2;
}
.lhh-q {
  margin: 0;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 12.5px;
  line-height: 19px;
  font-weight: 600;
  color: #6b4a00;
}
.lhh-q :deep(.ic) {
  flex: none;
  width: 15px;
  height: 15px;
  margin-top: 2px;
  color: #b86e00;
}
.lhh-acts {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.lhh-btn {
  height: 32px;
  padding: 0 12px 0 10px;
  border-radius: 8px;
  border: 1px solid var(--line);
  background: #fff;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  line-height: 20px;
  font-weight: 600;
  color: var(--ink);
}
.lhh-btn:hover:not(:disabled) {
  background: #f4f6f5;
}
.lhh-btn.yes {
  border-color: var(--ac);
  background: var(--ac);
  color: #fff;
}
.lhh-btn.yes:hover:not(:disabled) {
  background: var(--ac2);
}
.lhh-btn.warn {
  border-color: #e3c58a;
  color: #7a4700;
}
.lhh-btn:disabled {
  opacity: 0.6;
}
.lhh-text {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 28px;
  padding: 0 8px 0 6px;
  margin-left: -6px;
  border-radius: 7px;
  font-size: 12.5px;
  line-height: 19px;
  font-weight: 600;
  color: var(--mut);
  background: none;
}
.lhh-acts .lhh-text {
  align-self: center;
  margin-left: 0;
  color: var(--ac2);
}
.lhh-text:hover {
  background: #eef2f0;
}
.lhh-confirm {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #fff6e5;
}
.lhh-confirm-t {
  font-size: 12.5px;
  line-height: 19px;
  color: #6b4a00;
}
.lhh-l {
  margin: 0;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 12.5px;
  line-height: 19px;
  color: var(--mut);
}
.lhh-l :deep(.ic) {
  flex: none;
  width: 15px;
  height: 15px;
  margin-top: 2px;
}
.lhh-l.t-warn {
  color: #7a4700;
}
</style>
